"use client";

import Link, { type LinkProps } from "next/link";
import {
  type AnchorHTMLAttributes,
  type ComponentPropsWithRef,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useAnalytics } from "@/components/analytics/use-analytics";
import { resolveAppHandoffHref } from "@/lib/app-handoff";

type TrackingLinkProps = LinkProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
    location: string;
    label: string;
    children: ReactNode;
  };

/**
 * Googlebot runs the handoff effect too, so every render gave it a new
 * `?bp_vid=` app URL to crawl: 190+ of them sat in Search Console as blocked
 * or duplicate. `nofollow` keeps those links out of its crawl queue.
 */
export function withNofollow(rel: string | undefined): string {
  const tokens = new Set((rel ?? "").split(/\s+/).filter(Boolean));
  tokens.add("nofollow");
  return [...tokens].join(" ");
}

const TrackedWebAppLink = ({
  location,
  label,
  onClick,
  children,
  href,
  ref,
  ...props
}: TrackingLinkProps & { ref?: ComponentPropsWithRef<typeof Link>["ref"] }) => {
  const { trackWebAppButtonClick } = useAnalytics();
  // `clientHref` is what the <a> actually renders: the bare href until the
  // async resolution below folds in the handoff params. `handoffReady` is state
  // (not a ref) so it can only flip true in the SAME commit that renders the
  // resolved href — a click can never observe "ready" while the DOM still holds
  // the bare URL. Until ready, a click resolves the params in-band (see
  // handleClick) so a fast click can't drop gclid/bp_vid (issue #275).
  const [clientHref, setClientHref] = useState(href);
  const [handoffReady, setHandoffReady] = useState(false);
  const [trackedHref, setTrackedHref] = useState(href);

  // If the href prop changes, fall back to not-ready + the bare href during this
  // render (before the resolution effect re-runs), so a click can never navigate
  // to a resolved destination left over from the previous href.
  if (href !== trackedHref) {
    setTrackedHref(href);
    setClientHref(href);
    setHandoffReady(false);
  }

  // Resolve the visitor + click ids from the current URL and fold them into the
  // app handoff href. Persists any landing click ids first so the link carries
  // them even if it renders before BriefpanelAnalyticsTracker captures them.
  // Shared by the prefetch effect and the click-time race guard.
  const resolveHandoffHref = useCallback(
    (target: string) => resolveAppHandoffHref(target),
    [],
  );

  useEffect(() => {
    if (typeof href !== "string") {
      setHandoffReady(true);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const resolved = await resolveHandoffHref(href);
        if (cancelled) {
          return;
        }
        // Set together so readiness becomes observable only in the commit that
        // also renders the resolved href.
        setClientHref(resolved);
        setHandoffReady(true);
      } catch {
        // Resolution failed (e.g. sessionStorage is unavailable, which can throw
        // on write). Leave the bare href in place and stay not-ready so a primary
        // click retries in-band and modified clicks use the bare href.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [href, resolveHandoffHref]);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);

    if (event.defaultPrevented) {
      return;
    }

    trackWebAppButtonClick({ location, label });

    // Let the browser handle the navigation when the handoff href is already
    // resolved, or for clicks we must not hijack: non-string hrefs, modified
    // clicks (new tab/window), and non-primary buttons (middle/right click).
    if (
      handoffReady ||
      typeof href !== "string" ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    // Fast click before the effect resolved: hold navigation, resolve the
    // handoff in-band, then go — so the app opens with gclid/bp_vid attached.
    // Fall back to the bare href (never a stale resolved destination) on error.
    event.preventDefault();
    void (async () => {
      let destination = href;
      try {
        destination = await resolveHandoffHref(href);
      } catch {
        // Attribution is best-effort; navigate with the bare href.
      }
      window.location.assign(destination);
    })();
  };

  const linkProps = { ...props, rel: withNofollow(props.rel) };
  return (
    <Link {...linkProps} href={clientHref} ref={ref} onClick={handleClick}>
      {children}
    </Link>
  );
};

TrackedWebAppLink.displayName = "TrackedLink";

export default TrackedWebAppLink;
